import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { BrowserWindowSymbol, ExperimentsSymbol, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type { BrowserWindow, IAppWillMountHandler, Experiments } from '@wix/thunderbolt-symbols'
import { name, ReactTemplateSymbol } from './symbols'
import type { BuilderModuleLoaderSiteConfig, ImportMap, ReactTemplate } from './types'
import * as signals from '@preact/signals-react'
import { checkSupportsDynamicImportMaps } from './utils'

const ES_MODULE_SHIMS_URL = 'https://static.parastorage.com/services/thunderbolt-shims-static/1.0.0/es-module-shims.js'

// Served from Wix static CDN (thunderbolt-shims-static)
const USE_SYNC_EXTERNAL_STORE_SHIM_URL =
	'https://static.parastorage.com/services/thunderbolt-shims-static/1.0.0/use-sync-external-store-shim.js'

function createModuleUrl(code: string) {
	const blob = new Blob([code], { type: 'text/javascript' })
	return URL.createObjectURL?.(blob) || ''
}

declare global {
	interface Window {
		importShim: {
			(url: string): Promise<any>
		}
	}
}

export const ImportMapFactory = withDependencies(
	[ReactTemplateSymbol, BrowserWindowSymbol, ExperimentsSymbol, named(SiteFeatureConfigSymbol, name)],
	(
		reactTemplate: ReactTemplate,
		browserWindow: BrowserWindow,
		experiments: Experiments,
		siteConfig: BuilderModuleLoaderSiteConfig
	): IAppWillMountHandler & ImportMap => {
		let shouldUseEsModuleShims = false
		const useEsModuleShimsExperiment = !!experiments['specs.thunderbolt.useEsModuleShim']

		const shouldDisableBuilderWixComponents = !!experiments['specs.thunderbolt.disableBuilderWixComponents']
		const { esm: packageUrls } = siteConfig.builderComponentsData.publicPackagesUrls

		let pendingImportMap: Record<string, string> = {
			react: createModuleUrl(reactTemplate.getReactModuleCode('window.React')),
			'react-dom': createModuleUrl(reactTemplate.getReactDOMModuleCode('window.ReactDOM')),
			'react/jsx-runtime': createModuleUrl(reactTemplate.getReactJsxRuntimeCode()),
			'react/jsx-dev-runtime': createModuleUrl(reactTemplate.getReactJsxDevRuntimeCode()),
			'@preact/signals-react': createModuleUrl(reactTemplate.getPreactSignalsReactModuleCode('window.signals')),
			'use-sync-external-store/shim/index.js': USE_SYNC_EXTERNAL_STORE_SHIM_URL,

			'@wix/builder-interactions': packageUrls['builder-interactions'],
			'@wix/builder-state': packageUrls['builder-state'],
			'@wix/builder-state-react': packageUrls['builder-state-react'],
			'@wix/builder-velo': packageUrls['builder-velo'],
			'@wix/builder-velo-react': packageUrls['builder-velo-react'],
			...(!shouldDisableBuilderWixComponents && {
				'@wix/builder-wix-components': packageUrls['builder-wix-components'],
			}),
			'@wix/services-manager-react': packageUrls['services-manager-react'],
			'@wix/builder-element-removal': packageUrls['builder-element-removal'],
			'@wix/builder-utils': packageUrls['builder-utils'],
		}

		// tracks import specifiers that have been committed to the DOM import map to avoid duplicate commits
		const committedImportSpecifiers = new Set<string>(Object.keys(pendingImportMap))

		if (browserWindow) {
			;(browserWindow as any).signals = signals // TODO - https://wix.atlassian.net/browse/EDIX-31107
		}

		let commitId = 0
		const mapScriptId = 'builder-module-loader-importmap'

		const updateImportMap = (updates: Record<string, string>) => {
			Object.entries(updates).forEach(([key, url]) => {
				if (!committedImportSpecifiers.has(key)) {
					pendingImportMap[key] = url
				}
			})
		}

		// creates a new import map (existing import maps mutation is not supported)
		const commitImportMapUpdates = async () => {
			if (!browserWindow?.document) {
				return
			}
			if (Object.keys(pendingImportMap).length === 0) {
				// nothing to commit
				return
			}

			const mapScript = browserWindow.document.createElement('script')
			mapScript.id = `${mapScriptId}-${commitId}`
			mapScript.type = shouldUseEsModuleShims ? 'importmap-shim' : 'importmap'
			mapScript.textContent = JSON.stringify({ imports: pendingImportMap })
			browserWindow.document.head.appendChild(mapScript)

			Object.keys(pendingImportMap).forEach((specifier) => committedImportSpecifiers.add(specifier))

			pendingImportMap = {}
			commitId++
			await Promise.resolve()
		}

		const initEsModuleShims = async () => {
			return new Promise<void>((resolve) => {
				if (browserWindow) {
					;(browserWindow as any).esmsInitOptions = {
						shimMode: true,
					}
					const esModuleShimsScript = browserWindow.document.createElement('script')
					esModuleShimsScript.src = ES_MODULE_SHIMS_URL
					esModuleShimsScript.onload = () => resolve()
					browserWindow.document.head.appendChild(esModuleShimsScript)
				}
			})
		}

		const updateAndCommit = (dependencies: Record<string, string>) => {
			updateImportMap(dependencies)
			commitImportMapUpdates()
		}

		return {
			// runs in the editor only
			appWillMount: async () => {
				if (!useEsModuleShimsExperiment) {
					return
				}
				const supportsDynamicImportMaps = await checkSupportsDynamicImportMaps()
				if (!supportsDynamicImportMaps) {
					shouldUseEsModuleShims = true
					await initEsModuleShims()
				}
			},
			updateImportMap,
			commitImportMapUpdates,
			updateAndCommit,
		}
	}
)
